import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import axios from "axios";
import { useEffect, useState } from "react";
import { API_URL } from "../constants/config";
import { errorAlert, successAlert } from "../utils/toast";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "1px solid #ddd",
  boxShadow: 24,
  borderRadius: "4px",
  maxHeight: "80vh",
  overflowY: "auto",
};

const Spinner = () => (
  <svg
    className="animate-spin h-5 w-5 mr-3 text-white"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 0114.32-4.32A5 5 0 009 16.5A5 5 0 004 12z"
    />
  </svg>
);

const PromoCodeModal = (props) => {
  const {
    open,
    handleClose,
    isEditMode,
    selectedPromoCode,
    refreshPromoCodes,
  } = props;

  const [formData, setFormData] = useState({
    code: "",
    discount_type: "percentage",
    discount_value: 0,
    expiration_date: "",
    usage_limit: 0,
    min_report_count: 1, // Updated field name and default value
    status: "active",
    payment_usage_per_account: 0,
    max_report_count: 0, // Updated field name
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEditMode && selectedPromoCode) {
      setFormData({
        code: selectedPromoCode.code,
        discount_type: selectedPromoCode.discount_type,
        discount_value: selectedPromoCode.discount_value,
        expiration_date: selectedPromoCode.expiration_date,
        usage_limit: selectedPromoCode.usage_limit,
        min_report_count: selectedPromoCode.min_report_count, // Updated field name
        status: selectedPromoCode.status,
        payment_usage_per_account: selectedPromoCode.payment_usage_per_account,
        max_report_count: selectedPromoCode.max_report_count, // Updated field name
      });
    } else {
      setFormData({
        code: "",
        discount_type: "percentage",
        discount_value: 0,
        expiration_date: "",
        usage_limit: 0,
        min_report_count: 1, // Updated field name and default value
        status: "active",
        payment_usage_per_account: 0,
        max_report_count: 0, // Updated field name
      });
    }

    return () => {
      setFormData({
        code: "",
        discount_type: "percentage",
        discount_value: 0,
        expiration_date: "",
        usage_limit: 0,
        min_report_count: 1, // Updated field name and default value
        status: "active",
        payment_usage_per_account: 0,
        max_report_count: 0, // Updated field name
      });
    };
  }, [isEditMode, selectedPromoCode, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      let response;

      if (isEditMode) {
        response = await axios.put(
          `${API_URL}/promo-codes/${selectedPromoCode.id}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        response = await axios.post(`${API_URL}/promo-codes`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      if (response.status === 200) {
        successAlert(response.data.message);
      }

      setIsSubmitting(false);
      handleClose();
      refreshPromoCodes();
    } catch (error) {
      setIsSubmitting(false);
      console.log("error", error);
      if (error.status === 422 && error.response.data.message) {
        const errors = error.response.data.message;
        for (const field in errors) {
          if (errors.hasOwnProperty(field)) {
            errorAlert(errors[field][0]);
            break;
          }
        }
      } else {
        errorAlert(
          isEditMode
            ? "Error updating promo code!"
            : "Error creating promo code!"
        );
      }
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style} style={{ padding: "16px" }}>
        <div className="flex items-center justify-between p-4 border-b">
          <p className="font-bold text-lg">
            {isEditMode ? "Edit Promo Code" : "Create Promo Code"}
          </p>
          <button className="text-black" onClick={handleClose}>
            <CloseIcon />
          </button>
        </div>
        <div className="p-4">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block mb-2">Promo Code</label>
              <input
                required
                className="w-full border rounded-md px-2 py-1"
                name="code"
                value={formData.code}
                onChange={handleChange}
                type="text"
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2">Discount Type</label>
              <select
                name="discount_type"
                value={formData.discount_type}
                onChange={handleChange}
                required
                className="w-full border rounded-md px-2 py-1"
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block mb-2">Discount Value</label>
              <input
                required
                className="w-full border rounded-md px-2 py-1"
                name="discount_value"
                value={formData.discount_value}
                onChange={handleChange}
                type="number"
                step="0.01" // Allow decimal values
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2">Expiration Date</label>
              <input
                required
                className="w-full border rounded-md px-2 py-1"
                name="expiration_date"
                min={
                  isEditMode
                    ? formData.expiration_date
                    : new Date().toISOString().split("T")[0]
                }
                value={formData.expiration_date}
                onChange={handleChange}
                type="date"
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2">Usage Limit</label>
              <input
                required
                className="w-full border rounded-md px-2 py-1"
                name="usage_limit"
                value={formData.usage_limit}
                onChange={handleChange}
                type="number"
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2">Minimum Report Count</label>{" "}
              {/* Updated label */}
              <input
                required
                className="w-full border rounded-md px-2 py-1"
                name="min_report_count" // Updated field name
                value={formData.min_report_count}
                onChange={handleChange}
                type="number"
                min="1"
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2">Usage Per Account</label>
              <input
                required
                className="w-full border rounded-md px-2 py-1"
                name="payment_usage_per_account"
                value={formData.payment_usage_per_account}
                onChange={handleChange}
                type="number"
                min="0"
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2">Maximum Report Count</label>{" "}
              {/* Updated label */}
              <input
                required
                className="w-full border rounded-md px-2 py-1"
                name="max_report_count" // Updated field name
                value={formData.max_report_count}
                onChange={handleChange}
                type="number"
                min="0"
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
                className="w-full border rounded-md px-2 py-1"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex justify-between items-center">
              <button
                className="w-full rounded-md px-4 py-2 bg-cf--primary text-white font-extrabold transition-all hover:bg-cf--secondary"
                disabled={isSubmitting}
                type="submit"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <Spinner />
                    <span>Processing...</span>
                  </div>
                ) : isEditMode ? (
                  "Update Promo Code"
                ) : (
                  "Create Promo Code"
                )}
              </button>
            </div>
          </form>
        </div>
      </Box>
    </Modal>
  );
};

export default PromoCodeModal;
